import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { getTextWidth } from '@/utils/text-width'
import {
  DAY_MILLISECONDS,
  HOUR_MILLISECONDS,
  MONTH_MILLISECONDS,
  WEEK_MILLISECONDS,
  YEAR_MILLISECONDS
} from '@/utils/time'

export type TickState = 'complete' | 'ticks' | 'step' | 'count' | 'auto' | 'auto-date' | 'data'

export type Ticks<State extends TickState = TickState> = State extends 'complete'
  ? {
      values: number[]
      labels: string[]
      rotate?: number
    }
  : State extends 'ticks'
  ? {
      values: number[]
    }
  : State extends 'step'
  ? {
      step: number
      start?: number
      end?: number
    }
  : State extends 'count'
  ? {
      count: number
      start?: number
      end?: number
    }
  : State extends 'auto'
  ? 'auto'
  : State extends 'auto-date'
  ? 'auto-date'
  : State extends 'data'
  ? 'data'
  : never

// Function to create automatic ticks for a range
export function createAutoTicks(limits: [number, number]): Ticks<'ticks'> {
  // Destructure the limits into min and max
  // Example: limits = [3, 20] => min = 3, max = 20
  const [min, max] = limits[0] === limits[1] ? [limits[0] - 1, limits[1] + 1] : limits

  // Calculate the range of the limits
  // Example: range = 20 - 3 = 17
  const range = max - min

  // Calculate the step size
  // Example: step = 10^floor(log10(17)) = 10
  const step = Math.pow(10, Math.floor(Math.log10(range)))

  // Calculate the number of steps
  // Example: n = 17 / 10 = 1.7
  const n = range / step

  // Determine the final step size based on the number of steps (this is done to get enough ticks when the range is small)
  // Example: stepSize = 1.7 > 10 ? 10 * 2 : 1.7 > 2 ? 10 : 10 / 2 = 5
  let stepSize = n > 10 ? step * 2 : n > 2 ? step : step / 2
  if (stepSize < 0.1) {
    stepSize = 0.1
  }

  // Calculate the start value for the ticks
  // Example: start = floor(3 / 5) * 5 = 0
  const start = Math.floor(min / stepSize) * stepSize

  // Calculate the end value for the ticks
  // Example: end = ceil(20 / 5) * 5 + 5 / 1.7 = 25
  const end = Math.ceil(max / stepSize) * stepSize + stepSize / n

  // Return the values for the ticks
  // Example: values = [0, 5, 10, 15, 20, 25]
  return {
    values: Array.from({ length: (end - start) / stepSize + 1 }, (_, i) => start + i * stepSize)
  }
}

function createAutoDateTicks(limits: [number, number]): Ticks<'ticks'> {
  const [min, max] = limits
  const range = max - min

  const hours = range / HOUR_MILLISECONDS
  const days = range / DAY_MILLISECONDS
  const weeks = range / WEEK_MILLISECONDS
  const months = range / MONTH_MILLISECONDS

  // Find if use step hour, day, week, month, year
  let step = HOUR_MILLISECONDS
  if (hours < 24) {
    step = HOUR_MILLISECONDS
  } else if (days < 7) {
    step = DAY_MILLISECONDS
  } else if (weeks < 52) {
    step = WEEK_MILLISECONDS
  } else if (months < 12) {
    step = MONTH_MILLISECONDS
  } else {
    step = YEAR_MILLISECONDS
  }

  // Calculate the start value for the ticks
  const start = Math.floor(min / step) * step

  // Calculate the end value for the ticks
  const end = Math.ceil(max / step) * step

  // Return the values for the ticks
  return {
    values: Array.from({ length: (end - start) / step + 1 }, (_, i) => start + i * step)
  }
}

function createCountTicks(ticks: Ticks<'count'>, limits: [number, number]): Ticks<'ticks'> {
  const [min, max] = limits
  const { count, start = min, end = max } = ticks
  return {
    values: Array.from({ length: count }, (_, i) => start + (end - start) * (i / (count - 1)))
  }
}

function createStepTicks(ticks: Ticks<'step'>, limits: [number, number]): Ticks<'ticks'> {
  const [min, max] = limits
  const { step, start = min, end = max } = ticks
  return {
    values: Array.from({ length: (end - start) / step + 1 }, (_, i) => start + i * step)
  }
}

function createLabels(
  ticks: Ticks<'ticks'>,
  options: FormatOptions | undefined,
  locale: string
): Ticks<'complete'> {
  return {
    ...ticks,
    labels: ticks.values.map((tick) => formatter(tick, locale, options).toString())
  }
}

export const isCompleteTicks = (ticks: Ticks<TickState>): ticks is Ticks<'complete'> =>
  ticks instanceof Object &&
  'labels' in ticks &&
  'values' in ticks &&
  ticks.values.length > 0 &&
  ticks.labels.length > 0
export const isAutoTicks = (ticks: Ticks<TickState>): ticks is Ticks<'auto'> => ticks === 'auto'
export const isDataTicks = (ticks: Ticks<TickState>): ticks is Ticks<'data'> => ticks === 'data'
export const isAutoDateTicks = (ticks: Ticks<TickState>): ticks is Ticks<'auto-date'> =>
  ticks === 'auto-date'
export const isStepTicks = (ticks: Ticks<TickState>): ticks is Ticks<'step'> =>
  ticks instanceof Object && 'step' in ticks
export const isCountTicks = (ticks: Ticks<TickState>): ticks is Ticks<'count'> =>
  ticks instanceof Object && 'count' in ticks
export const isTicks = (ticks: Ticks<TickState>): ticks is Ticks<'ticks'> =>
  ticks instanceof Object && !('labels' in ticks) && 'values' in ticks && ticks.values.length > 0

export function createTicks<State extends TickState>(
  ticks: Ticks<State>,
  limits: [number, number],
  values: number[],
  format: FormatOptions | undefined,
  locale: string
): Ticks<'complete'> {
  let completeTicks: Ticks<'complete'> | undefined = undefined
  if (isDataTicks(ticks)) {
    completeTicks = createLabels({ values }, format, locale)
  } else if (isAutoTicks(ticks)) {
    completeTicks = createLabels(createAutoTicks(limits), format, locale)
  } else if (isAutoDateTicks(ticks)) {
    completeTicks = createLabels(createAutoDateTicks(limits), format, locale)
  } else if (isStepTicks(ticks)) {
    completeTicks = createLabels(createStepTicks(ticks, limits), format, locale)
  } else if (isCountTicks(ticks)) {
    completeTicks = createLabels(createCountTicks(ticks, limits), format, locale)
  } else if (isTicks(ticks)) {
    completeTicks = createLabels(ticks, format, locale)
  } else if (isCompleteTicks(ticks)) {
    completeTicks = ticks
  } else {
    throw new Error('Invalid ticks:', ticks)
  }

  return completeTicks
}

function scale(value: number, values: number[], viewportSize: number): number {
  const max = Math.max(...values)
  const min = Math.min(...values)
  return (viewportSize * value) / (max - min)
}

function calclateMinimumGap(values: number[], labels: string[], viewportSize: number): number {
  return scale(
    values.reduce((a, b, i) => {
      if (i === 0 || !labels[i] || !labels[i - 1]) return a
      return Math.min(a, b - values[i - 1])
    }, Infinity),
    values,
    viewportSize
  )
}

function calculateLabelOverlap(
  values: number[],
  labels: string[],
  viewportSize: number,
  font: string
): number {
  const gap = calclateMinimumGap(values, labels, viewportSize)

  const longestLabel = labels.reduce(
    (a, b) => (getTextWidth(a, font) > getTextWidth(b, font) ? a : b),
    ''
  )
  const labelWidth = getTextWidth(longestLabel, font)
  const overlap = labelWidth - gap
  const relativeOverlap = overlap / labelWidth
  return relativeOverlap
}

function everyOtherIndex(labels: string[]): number[] {
  return labels.map((_, i) => i).filter((i) => i % 2 === 0)
}

export function responsiveLabels(
  completeTicks: Ticks<'complete'>,
  viewportSize: number
): Ticks<'complete'> {
  let { values, labels } = completeTicks
  values = values.slice().filter((v, i) => labels[i])
  labels = labels.slice().filter((l) => l)

  const fontSize = 10
  const font = `${fontSize}px Inter`

  const calcRange = (x, start, stop) => (Math.min(x, stop) - start) / (stop - start)

  const rotateStart = -0.25
  const rotateStop = 0.5

  let labelOverlap = calculateLabelOverlap(values, labels, viewportSize, font)
  let rotate =
    labelOverlap > rotateStart ? calcRange(labelOverlap, rotateStart, rotateStop) * 45 : 0

  for (let i = 0; i < 1000 && labelOverlap > 0.55; i++) {
    const indicesToKeep = everyOtherIndex(labels)
    labels = indicesToKeep.map((i) => labels[i])
    values = indicesToKeep.map((i) => values[i])
    labelOverlap = calculateLabelOverlap(values, labels, viewportSize, font)
  }

  labelOverlap = calculateLabelOverlap(values, labels, viewportSize, font)
  rotate = labelOverlap > rotateStart ? calcRange(labelOverlap, rotateStart, rotateStop) * 45 : 0

  completeTicks.rotate = rotate
  completeTicks.labels = labels
  completeTicks.values = values

  return completeTicks
}
